/**
 * Tour step definitions for desktop and mobile
 */

export type TooltipPosition = "top" | "bottom" | "left" | "right" | "center";

export type DesktopTourStep = {
  id: string;
  targetSelector: string;
  title: string;
  description: string;
  tooltipPosition: TooltipPosition;
  highlightPadding?: number;
};

export type MobileTourStep = {
  id: string;
  title: string;
  description: string;
  illustration: "welcome" | "markets" | "trade" | "portfolio" | "navigation" | "complete";
};

export const DESKTOP_TOUR_STEPS: DesktopTourStep[] = [
  {
    id: "asset-selector",
    targetSelector: '[data-tour="asset-selector"]',
    title: "Select Your Market",
    description: "Choose the cryptocurrency market you want to trade. Click here to browse available trading pairs like BTC-USD, ETH-USD, and more.",
    tooltipPosition: "bottom",
    highlightPadding: 8,
  },
  {
    id: "ticker-stats",
    targetSelector: '[data-tour="ticker-stats"]',
    title: "Price Information",
    description: "Market Price shows the current trading price from live feeds. Oracle Price is the on-chain reference price used for liquidations and funding calculations.",
    tooltipPosition: "bottom",
    highlightPadding: 8,
  },
  {
    id: "chart",
    targetSelector: '[data-tour="chart"]',
    title: "Price Chart",
    description: "Real-time price chart powered by TradingView. Use the toolbar to add indicators, change timeframes, and analyze market trends.",
    tooltipPosition: "bottom",
    highlightPadding: 4,
  },
  {
    id: "positions",
    targetSelector: '[data-tour="positions"]',
    title: "Positions & Orders",
    description: "View your open positions, pending orders, trade history, and funding payments. Everything you need to manage your trades in one place.",
    tooltipPosition: "top",
    highlightPadding: 4,
  },
  {
    id: "order-book",
    targetSelector: '[data-tour="order-book"]',
    title: "Order Book",
    description: "See the market depth with buy and sell orders. This panel shows liquidity and helps you understand market sentiment. You can toggle this on or off anytime in Settings for a cleaner view.",
    tooltipPosition: "left",
    highlightPadding: 4,
  },
  {
    id: "account-panel",
    targetSelector: '[data-tour="account-panel"]',
    title: "Account Health",
    description: "Monitor your account's margin ratio and health status. This shows how close you are to liquidation and your available balance. You can toggle this panel on or off in Settings.",
    tooltipPosition: "left",
    highlightPadding: 4,
  },
  {
    id: "margin-mode",
    targetSelector: '[data-tour="margin-mode"]',
    title: "Margin Mode",
    description: "Isolated mode risks only this position's collateral. Cross mode shares margin across all positions. We recommend Isolated for beginners—it's safer.",
    tooltipPosition: "left",
    highlightPadding: 8,
  },
  {
    id: "leverage-button",
    targetSelector: '[data-tour="leverage-button"]',
    title: "Leverage",
    description: "Adjust your leverage here. Higher leverage means higher potential gains but also higher risk. Start with lower leverage (2-5x) until you're comfortable.",
    tooltipPosition: "left",
    highlightPadding: 8,
  },
  {
    id: "available-balance",
    targetSelector: '[data-tour="available-balance"]',
    title: "Your Available Balance",
    description: "This shows how much you have available to trade with. Your balance is calculated from your deposited funds minus any margin already used by open positions.",
    tooltipPosition: "left",
    highlightPadding: 8,
  },
  {
    id: "order-input",
    targetSelector: '[data-tour="order-input"]',
    title: "Enter Your Trade Amount",
    description: "Type in how much you want to trade. You can enter an amount in USD or switch to the base currency (like BTC). Use the percentage slider below to quickly set a portion of your available balance.",
    tooltipPosition: "left",
    highlightPadding: 8,
  },
  {
    id: "trade-execution",
    targetSelector: '[data-tour="trade-execution-area"]',
    title: "Execute Your Trade",
    description: "When you're ready, click Buy/Long if you think the price will go up, or Sell/Short if you think it will go down. The liquidation prices show where your position would be automatically closed if the market moves against you.",
    tooltipPosition: "left",
    highlightPadding: 8,
  },
  {
    id: "complete",
    targetSelector: '[data-tour="complete-step"]',
    title: "You're All Set!",
    description: "You've completed the platform tour. You can restart this guide anytime from Settings. Happy trading!",
    tooltipPosition: "center",
  },
];

export const MOBILE_TOUR_STEPS: MobileTourStep[] = [
  {
    id: "welcome",
    title: "Welcome to Nordex",
    description: "Let's take a quick tour of the mobile trading experience. The platform is optimized for trading on the go.",
    illustration: "welcome",
  },
  {
    id: "markets",
    title: "Markets Tab",
    description: "Tap the market selector at the top to browse and switch between trading pairs. View price charts, 24h change, and volume at a glance.",
    illustration: "markets",
  },
  {
    id: "trade",
    title: "Trade Tab",
    description: "This is where you place orders. Select your order type, enter the amount, and use the Buy/Sell buttons. The chart shows real-time price action.",
    illustration: "trade",
  },
  {
    id: "portfolio",
    title: "Account",
    description: "View your open positions, order history, and account balance. Manage your trades and track your performance.",
    illustration: "portfolio",
  },
  {
    id: "navigation",
    title: "Bottom Navigation",
    description: "Use the bottom navigation bar to quickly switch between Markets, Trade, and your Account. Everything is one tap away.",
    illustration: "navigation",
  },
  {
    id: "complete",
    title: "Ready to Trade!",
    description: "You've completed the tour. You can restart this guide anytime from Settings. Good luck with your trading!",
    illustration: "complete",
  },
];

